/* eslint-disable id-length */
import { useEffect, useRef } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import annotationPlugin from 'chartjs-plugin-annotation';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import PropTypes from 'prop-types';

import styles from './chart.module.scss';

ChartJS.register(
  LinearScale,
  CategoryScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  ChartDataLabels,
  annotationPlugin,
  Tooltip,
  Legend,
);

Tooltip.positioners.point = function (_, eventPosition) {
  return {
    x: eventPosition.x,
    y: eventPosition.y,
  };
};

const Chart = ({
  data,
  customOptions, // supports only for top level and 1 level deep options
  containerWidth,
  containerHeight,
  centerText = null,
  handleSegmentClick,
  chartType,
  containerClass = null,
  onMouseLeave,
  onChartMouseMove,
}) => {
  const chartRef = useRef(null);
  const timeoutIds = useRef([]);
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    devicePixelRatio: window.devicePixelRatio,
    plugins: {
      datalabels: {
        display: false, //  hides values on segments
      },
      legend: {
        display: false, //  hides default legend
      },
      tooltip: {
        backgroundColor: '#FFFFFF',
        bodyColor: '#49454E',
        borderColor: '#DDD',
        borderWidth: 1,
        position: 'point',
        displayColors: false,
        bodyFont: {
          family: 'Mukta',
          weight: 400,
          size: 12, // in px
        },
        callbacks: {
          label: function (context) {
            return context.label;
          },
          title: function () {
            return null; // Don't display the title
          },
        },
      },
    },
  };
  const keyOptions = [
    'responsive',
    'plugins',
    'scales',
    'maintainAspectRatio',
    'animation',
    'devicePixelRatio',
    'onHover',
  ]; // Add any other top-level options you want to allow

  for (const key in customOptions) {
    if (keyOptions.includes(key)) {
      options[key] = customOptions[key];
    } else {
      const [mainKey, subKey] = key.split('.');

      if (options[mainKey]) {
        options[mainKey] = {
          ...options[mainKey],
          [subKey]: customOptions[key],
        };
      } else {
        options[mainKey] = {
          [subKey]: customOptions[key],
        };
      }
    }
  }

  const getClickedSegmentData = (event) => {
    const chart = chartRef.current;
    if (!chart) return null;
    const elements = chart.getElementsAtEventForMode(
      event.nativeEvent,
      'nearest',
      { intersect: true },
      false,
    );

    if (Array.isArray(elements) && elements[0]) {
      const { datasetIndex, index } = elements[0];
      const dataset = data.datasets[datasetIndex];
      const label = data.labels[index];
      const value = dataset.data[index];

      return {
        label,
        value,
        mousePosition: {
          mouseX: event.clientX,
          mouseY: event.clientY,
        },
      };
    }
  };

  const handleClick = (event) => {
    if (!handleSegmentClick) {
      return;
    }
    const timeoutId = setTimeout(() => {
      //    setting timeout for double click detection
      const segmentData = getClickedSegmentData(event);
      if (!segmentData) return;
      handleSegmentClick(segmentData, 'single');
    }, 250);
    timeoutIds.current.push(timeoutId);
  };

  const handleDoubleClick = (event) => {
    if (!handleSegmentClick) {
      return;
    }
    // Cancel single click timeouts if double click is detected
    for (const timeoutId of timeoutIds.current) {
      clearTimeout(timeoutId);
    }
    const segmentData = getClickedSegmentData(event);
    if (!segmentData) return;
    handleSegmentClick(segmentData, 'double');
  };

  const handleMouseMove = (event) => {
    if (onChartMouseMove && chartRef.current) {
      onChartMouseMove({ event, chart: chartRef.current });
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (chartRef.current) {
        chartRef.current.resize();
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.update();
    }
  }, [data]);

  let chartToBeRendered = null;

  if (chartType === 'doughnut') {
    chartToBeRendered = (
      <>
        <Doughnut
          ref={chartRef}
          data={data}
          options={options}
        />
        {centerText}
      </>
    );
  }

  if (chartType === 'bar') {
    chartToBeRendered = (
      <Bar
        ref={chartRef}
        data={data}
        options={options}
      />
    );
  }

  if (chartType === 'line') {
    chartToBeRendered = (
      <Line
        ref={chartRef}
        data={data}
        options={options}
      />
    );
  }

  return (
    <button
      type="button"
      style={{
        width: containerWidth,
        height: containerHeight,
      }}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={onMouseLeave}
      className={
        containerClass
          ? [styles.container, containerClass].join(' ')
          : styles.container
      }
    >
      {chartToBeRendered}
    </button>
  );
};

export default Chart;

Chart.propTypes = {
  data: PropTypes.object.isRequired,
  customOptions: PropTypes.object,
  containerWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  containerHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  canvasWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  canvasHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  centerText: PropTypes.element,
  handleSegmentClick: PropTypes.func,
  chartType: PropTypes.string,
  containerClass: PropTypes.string,
  onMouseLeave: PropTypes.func,
  onChartMouseMove: PropTypes.func,
};
